import * as _ from "lodash";
import * as angular from "angular";
import { FieldDataProvider } from "../../data-provider/field.dp";
import { Integration } from "../../models/integration.model";
import { DialogUtils } from "../../helper/dialog.helper";
import { IntegrationDataProvider } from "../../data-provider/integration.dp";
import { VoterFileDataProvider } from "../../data-provider/voter-file.dp";

let IntegrationFieldMapping = {
    selector: "vcIntegrationFieldMapping",
    templateUrl:
        "/angular/components/integration/integration-field-mapping.component.html",
    bindings: {
        selectedIntegration: "=",
    },
    controller: class IntegrationFieldMappingController {
        // * bindings for typescript compiler *
        public selectedIntegration: Integration;
        // ************************************

        public readonly disabledFields = ["LastName", "FirstName"];
        public readonly hiddenFields = {
            callhub: [
                "firstname",
                "lastname",
                "mobilenumber",
                "phonenumber",
                "countrycode",
                "email",
                "address",
                "city",
                "state",
                "zipcode",
            ],
            salesforce: [],
        };

        public integration: Integration;
        public fields: any;
        public availableFieldNames: Set<string> | null = null;
        public fieldCategories: any;
        public columnname: string | undefined;
        public saving = false;

        private readonly $scope: any;
        private readonly $mdSelect: any;

        private readonly dialog: DialogUtils;
        private readonly dataProvider: IntegrationDataProvider;
        private readonly voterFileDataProvider: VoterFileDataProvider;
        private readonly fieldDataProvider: FieldDataProvider;

        constructor(
            $scope: any,
            $mdSelect: any,
            DialogUtils: DialogUtils,
            IntegrationDataProvider: IntegrationDataProvider,
            VoterFileDataProvider: VoterFileDataProvider,
            FieldDataProvider: FieldDataProvider
        ) {
            this.$scope = $scope;
            this.$mdSelect = $mdSelect;

            this.dialog = DialogUtils;
            this.dataProvider = IntegrationDataProvider;
            this.voterFileDataProvider = VoterFileDataProvider;
            this.fieldDataProvider = FieldDataProvider;
        }

        $onInit() {
            this.integration = _.cloneDeep(this.selectedIntegration);

            let type;
            if (this.integration.key == "salesforce") {
                type = this.selectedIntegration.settings.type;
            }

            if (
                !this.integration.settings ||
                this.integration.settings.length == 0
            ) {
                this.integration.settings = {};
            }

            if (!this.integration.settings.mapping) {
                if (this.integration.key == "salesforce") {
                    this.integration.settings.mapping = _.cloneDeep(
                        this.dataProvider.defaultMapping[this.integration.key][
                            type
                        ]
                    );
                } else {
                    this.integration.settings.mapping = _.cloneDeep(
                        this.dataProvider.defaultMapping[this.integration.key]
                    );
                }
            }

            this.dataProvider
                .getFields(this.integration.key)
                .then((fields: any) => {
                    this.fields = fields;
                    this.availableFieldNames = new Set(
                        (fields || []).map((field: any) => field.name)
                    );
                    this.reconcileMapping();
                });

            this.voterFileDataProvider.getFields().then((fields: any) => {
                this.fieldCategories = fields;

                this.fieldDataProvider
                    .getCustomFields()
                    .then((customFields: any) => {
                        let customFieldChoices = [];
                        for (let i = 0; i < customFields.length; i++) {
                            customFieldChoices.push({
                                columnname: customFields[i].name,
                                columnid: "cf" + customFields[i].cfid,
                                cfid: customFields[i].cfid,
                            });
                        }
                        this.fieldCategories[4] = {
                            columncategory: "Custom Fields",
                            columncategorysort: "5",
                            columns: customFieldChoices,
                        };
                    });
            });
        }

        ecanFieldMapped(columnId: string | number): boolean {
            const mapping = this.integration.settings.mapping;
            for (let key in mapping) {
                if (!mapping.hasOwnProperty(key)) {
                    continue;
                }
                // A mapping only counts as "in use" if its source field
                // actually exists on this account. Otherwise a phantom mapping
                // (e.g. an "Other*" field the org doesn't have) would grey out
                // its Ecanvasser target in every dropdown, with no visible card
                // to Remove it. See reconcileMapping().
                if (
                    this.availableFieldNames &&
                    !this.availableFieldNames.has(key)
                ) {
                    continue;
                }
                if (mapping[key] === columnId) {
                    return true;
                }
            }
            return false;
        }

        /**
         * Reconcile the seeded/saved mapping against the fields that actually
         * exist on this integration account. Salesforce orgs differ in which
         * standard fields are present — notably the "Other*" address fields
         * only exist on some accounts, yet the default mapping pre-maps our
         * address columns to them. Any mapping pointing at an absent field
         * would (a) grey out its Ecanvasser target with no card to unset it,
         * and (b) be saved/synced against a field that isn't there.
         *
         * So for each mapping whose source field is missing we first fall back
         * to the equivalent "Mailing*" field where one exists, then drop any
         * mapping that still points at an unavailable field.
         */
        reconcileMapping(): void {
            const mapping = this.integration.settings.mapping;
            const available = this.availableFieldNames;
            if (!mapping || !available) {
                return;
            }

            Object.keys(mapping).forEach((key) => {
                if (mapping[key] == null || available.has(key)) {
                    return;
                }

                // Back-up: remap an absent "Other*" address field onto the
                // equivalent "Mailing*" field if the account has it and it is
                // not already mapped.
                const fallback =
                    key.indexOf("Other") === 0
                        ? "Mailing" + key.substring("Other".length)
                        : null;

                if (
                    fallback &&
                    available.has(fallback) &&
                    mapping[fallback] == null
                ) {
                    mapping[fallback] = mapping[key];
                }

                delete mapping[key];
            });
        }

        mappingSelected(): void {
            this.$mdSelect.hide();
        }

        fieldHidden(name: string): boolean {
            return this.hiddenFields[this.integration.key].includes(name);
        }

        openFieldDialog(ev: any, field: any): void {
            if (!field && this.columnname) {
                field = { name: this.columnname };
            }

            this.dialog
                .show({
                    component: "vc-custom-field-form",
                    targetEvent: ev,
                    scope: this.$scope,
                    size: "large",
                    locals: {
                        bindedField: field,
                    },
                })
                .then((editedField: any) => {
                    this.fieldCategories[4].columns.unshift({
                        columnname: editedField.name,
                        columnid: "cf" + editedField.cfid,
                        cfid: editedField.cfid,
                    });
                });
        }

        confirm(confirmIntegration: Integration): void {
            this.saving = true;
            this.dataProvider
                .updateSettings(
                    confirmIntegration.key,
                    confirmIntegration.settings
                )
                .then(() => this.dialog.hide(confirmIntegration));
        }

        dismissDialog(): void {
            this.dialog.cancel();
        }
    },
};

angular
    .module("integration")
    .component(IntegrationFieldMapping.selector, IntegrationFieldMapping);
